import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import { ProxyContext } from '../enums/proxy-context.enum';
import { HttpRequestConfigurations } from '../models/http-request-configurations.model';
import { HttpServerResponse } from '../models/response.model';

@Injectable({
  providedIn: 'root'
})
export class HttpRequestService {
  baseUrl: string = '';

  constructor(@Inject('BASE_URL') private readonly baseUrlfrom: string, private readonly httpClient: HttpClient) {
    this.baseUrl = baseUrlfrom;
  }

  /**
   * HTTP GET Centralized action
   *
   * @param path - Specifies the proxy context to conect to backend
   * @param command - Command data to send in the request
   * @param configs - Configurations to apply to request
   * @returns - Request result as Observable value
   */
  get<TResponse>(path: ProxyContext, command?: unknown, configs?: HttpRequestConfigurations): Observable<HttpServerResponse<TResponse>> {
    let params = new HttpParams();
    if (command !== null && command !== undefined) {
      for (const [key, value] of Object.entries(command)) {
        params = params.set(key, value);
      }
    }

    let headers = new HttpHeaders();
    if (configs?.timeout != null) {
      headers = headers.set('timeout', configs?.timeout.toString());
    }

    if (configs?.useCache === true) {
      headers = headers.set('use-cache', 'true');
    }

    const options = configs?.timeout !== undefined || configs?.useCache ? { headers: headers, params } : { params };
    return this.httpClient
      .get<HttpServerResponse<TResponse>>(`${this.baseUrl}${path}`, options)
      .pipe(catchError((response: HttpErrorResponse) => this.processErrors(response, configs)));
  }

  /**
   * HTTP POST Centrilized action
   *
   * @param path - Specifies the proxy context to conect to backend
   * @param command - Command data to send in the request
   * @param configs - Configurations to apply to request
   * @returns - Result of request as Observable
   */
  post<TResponse>(path: ProxyContext, command?: unknown, configs?: HttpRequestConfigurations): Observable<HttpServerResponse<TResponse>> {
    let headers = new HttpHeaders();
    if (configs?.timeout !== undefined) {
      headers = headers.set('timeout', configs.timeout.toString());
    }
    const options = configs?.timeout !== undefined ? { headers: headers } : {};
    return this.httpClient
      .post<HttpServerResponse<TResponse>>(`${this.baseUrl}${path}`, command, options)
      .pipe(catchError((response: HttpErrorResponse) => this.processErrors(response, configs)));
  }

  /**
   * HTTP PUT Centralize action
   *
   * @param path - Specifies the proxy context to conect to backend
   * @param command - Command data to send in the request
   * @returns - Result of request as Observable
   */
  put<TResponse>(path: ProxyContext, command: unknown = {}): Observable<HttpServerResponse<TResponse>> {
    return this.httpClient.put<HttpServerResponse<TResponse>>(`${this.baseUrl}${path}`, command).pipe(catchError(this.formatErrors));
  }

  /**
   * HTTP DELETE Centralized actions
   *
   * @param path - Specifies the proxy context to conect to backend
   * @returns - Result of request as Observable
   */
  delete<TResponse>(path: ProxyContext, body: unknown = {}): Observable<HttpServerResponse<TResponse>> {
    return this.httpClient.delete<HttpServerResponse<TResponse>>(`${this.baseUrl}${path}`, { body }).pipe(catchError(this.formatErrors));
  }

  /**
   * HTTP PATCH Centrilized action
   *
   * @param path - Specifies the proxy context to conect to backend
   * @param command - Command data to send in the request
   * @returns - Result of request as Observable
   */
  patch<TResponse>(path: ProxyContext, command: unknown = {}): Observable<HttpServerResponse<TResponse>> {
    return this.httpClient.patch<HttpServerResponse<TResponse>>(`${this.baseUrl}${path}`, command).pipe(catchError(this.formatErrors));
  }

  /**
   * Process error to send
   *
   * @param response - Origin error response from server
   * @param configs - Request configurations
   */
  private processErrors(response: HttpErrorResponse, configs?: HttpRequestConfigurations): Observable<never> {
    if (configs?.formatErrors) {
      throw this.formatErrors(response);
    } else {
      throw response;
    }
  }

  /**
   * Process error to send
   *
   * @param response - Origin error response from server
   * @returns - Throws error to origin
   */
  private formatErrors(response: HttpErrorResponse): Observable<never> {
    return throwError(() => response.error);
  }
}
